/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable no-console */
/**
 * Client-side tool executors for the TelosAir Insight Agent.
 *
 * The model + agent loop run on the Node AI server, but these tools execute
 * in the browser: some read directly from the already-loaded data context,
 * others call existing dashboard APIs (which need the user's auth token).
 * The Node server declares matching tool *schemas* (telosair/ai-server) and
 * forwards each tool call here via the AI SDK's client-side tool mechanism.
 *
 * Each executor returns a small JSON payload (for the model) and optionally a
 * `display` block that the chat UI renders inline (charts, KPI cards).
 *
 * IMPORTANT: keep tool names + argument shapes in sync with the server
 * schemas in ai-server/src/agent/tools.ts.
 */

import { DeviceAttributes, ParametersType, ParticleData } from '../constants/telosair-interfaces';
import { ALL_PARAMETERS, DEFAULT_UNITS } from '../constants/global-constants';
import { getDevicesSummaryOverTime, getGlobalAirQuality } from './dashboard-service';
import { AgentToolExecutors, ChartSeries, MetricCard, RenderBlock, ToolResult, UploadedDataset } from './ai-agent.service';
import { ChartSettings } from '../components/AiAgent/AgentBlocks';

export interface AgentDataAccess {
  /** Latest snapshot summary keyed by parameter (already in data context). */
  dataSummary: ParticleData;
  /** Per-device latest summary. */
  deviceDataSummary: Record<string, { device: string; parameters: ParticleData }>;
  /** All devices for resolving names <-> serials. */
  devices: DeviceAttributes[];
  /**
   * Apply a partial update to the page-wide chart appearance. Used by the
   * `update_chart_appearance` tool to honour requests like "make lines red"
   * or "use white axis text". All values are re-validated by the page-level
   * `clampChartSettings` before being committed.
   */
  updateChartSettings: (partial: Partial<ChartSettings>) => void;
  /**
   * The dataset the user has uploaded for this turn (CSV / Excel parsed in
   * the browser), or null if nothing is attached. Surfaced to the model only
   * via the `read_uploaded_dataset` tool — never auto-included in the prompt.
   */
  uploadedData: UploadedDataset | null;
  /**
   * How many days of history the tenant's plan allows, or null for unlimited.
   * Reported by `list_capabilities` so the agent can size a date range before
   * the API rejects it (the server clamps this too — see
   * PlanDateRangeValidator in the dashboards repo).
   */
  historyDays?: number | null;
}

const SUPPORTED_PARAMS: ParametersType[] = [
  'temp',
  'hum',
  'co2',
  'pm25_m',
  'pm100_m',
  'tvoc',
  'pn03_m',
  'pressure',
  'aqi',
  'co',
  'no2',
  'o3',
  'so2',
  'ch4'
];

const PRETTY_NAME: Record<string, string> = {
  temp: 'Temperature',
  hum: 'Humidity',
  co2: 'CO₂',
  pm25_m: 'PM2.5',
  pm100_m: 'PM10',
  tvoc: 'TVOC',
  pn03_m: 'PN0.3',
  pressure: 'Pressure',
  aqi: 'AQI',
  co: 'CO',
  no2: 'NO₂',
  o3: 'Ozone',
  so2: 'SO₂',
  ch4: 'Methane'
};

/**
 * Maps an internal sensor parameter code to the names public networks (OpenAQ /
 * WAQI) use for the same pollutant, so a global measurement can be matched to
 * the sensor parameter being compared. Matched case-insensitively.
 */
const GLOBAL_PARAM_ALIASES: Record<string, string[]> = {
  pm25_m: ['pm25', 'pm2.5'],
  pm100_m: ['pm10'],
  co: ['co'],
  no2: ['no2'],
  o3: ['o3', 'ozone'],
  so2: ['so2'],
  temp: ['temperature', 'temp', 't'],
  hum: ['relativehumidity', 'humidity', 'rh', 'h'],
  pressure: ['pressure', 'p'],
  co2: ['co2'],
  aqi: ['aqi']
};

/**
 * Find the first measurement on a global station whose parameter matches the
 * given internal sensor code (via {@link GLOBAL_PARAM_ALIASES}).
 * @param station - A normalized station node from the air-quality proxy.
 * @param internalParam - Internal sensor parameter code (e.g. "pm25_m").
 * @returns The matching measurement, or undefined.
 */
function matchGlobalMeasurement(
  station: any,
  internalParam: string
): { parameter: string; value: number; unit: string } | undefined {
  const aliases = (GLOBAL_PARAM_ALIASES[internalParam] || [internalParam]).map((a) =>
    a.toLowerCase()
  );
  return (station?.measurements || []).find((m: any) =>
    aliases.includes(String(m.parameter || '').toLowerCase())
  );
}

/**
 * Gauge dial ranges and good/warn/bad threshold bands per parameter.
 * Zones are ascending by `upTo`; a value picks the first band it falls within.
 */
const GAUGE_CONFIG: Record<
  string,
  { min: number; max: number; zones: { upTo: number; tone: 'good' | 'warn' | 'bad' }[] }
> = {
  co2: { min: 400, max: 2000, zones: [{ upTo: 800, tone: 'good' }, { upTo: 1200, tone: 'warn' }, { upTo: 2000, tone: 'bad' }] },
  pm25_m: { min: 0, max: 150, zones: [{ upTo: 12, tone: 'good' }, { upTo: 35, tone: 'warn' }, { upTo: 150, tone: 'bad' }] },
  pm100_m: { min: 0, max: 200, zones: [{ upTo: 54, tone: 'good' }, { upTo: 154, tone: 'warn' }, { upTo: 200, tone: 'bad' }] },
  aqi: { min: 0, max: 500, zones: [{ upTo: 50, tone: 'good' }, { upTo: 150, tone: 'warn' }, { upTo: 500, tone: 'bad' }] },
  tvoc: { min: 0, max: 1000, zones: [{ upTo: 220, tone: 'good' }, { upTo: 660, tone: 'warn' }, { upTo: 1000, tone: 'bad' }] },
  temp: { min: 0, max: 45, zones: [{ upTo: 18, tone: 'warn' }, { upTo: 27, tone: 'good' }, { upTo: 45, tone: 'bad' }] },
  hum: { min: 0, max: 100, zones: [{ upTo: 30, tone: 'warn' }, { upTo: 60, tone: 'good' }, { upTo: 100, tone: 'warn' }] },
  co: { min: 0, max: 50, zones: [{ upTo: 9, tone: 'good' }, { upTo: 35, tone: 'warn' }, { upTo: 50, tone: 'bad' }] },
  no2: { min: 0, max: 200, zones: [{ upTo: 53, tone: 'good' }, { upTo: 100, tone: 'warn' }, { upTo: 200, tone: 'bad' }] },
  o3: { min: 0, max: 200, zones: [{ upTo: 54, tone: 'good' }, { upTo: 124, tone: 'warn' }, { upTo: 200, tone: 'bad' }] },
  so2: { min: 0, max: 200, zones: [{ upTo: 35, tone: 'good' }, { upTo: 75, tone: 'warn' }, { upTo: 200, tone: 'bad' }] }
};

function paramColor(p: string): string {
  return ALL_PARAMETERS[p as ParametersType]?.color || '#6366F1';
}

/**
 * Distinct line colours used when a single parameter is split into one line
 * per device. Cycled if the device count exceeds the palette length.
 */
const DEVICE_LINE_COLORS = [
  '#6366F1',
  '#F59E0B',
  '#10B981',
  '#EF4444',
  '#3B82F6',
  '#EC4899',
  '#14B8A6',
  '#A855F7'
];

function paramUnit(p: string): string {
  return DEFAULT_UNITS[p as ParametersType] || '';
}

const HOUR_MS = 3600 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** A resolved query window, ready to send to the dashboard API. */
interface ResolvedWindow {
  startDate: number;
  endDate: number;
  /** Span in whole hours — echoed to the model and used in filenames. */
  periodHours: number;
  /** Human label for titles and prose ("last 24h", "Jul 7 – Jul 14, 2026"). */
  label: string;
  /** True when the model supplied an explicit start/end instead of a lookback. */
  absolute: boolean;
  /** Adjustments made to the requested window, relayed to the model. */
  notes: string[];
}

const DATE_ONLY = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const MONTH_ONLY = /^(\d{4})-(\d{1,2})$/;
const LOCAL_DATETIME =
  /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

/**
 * Interpret one end of a model-supplied window.
 *
 * Bare dates and offset-less datetimes are read in the BROWSER's local time —
 * the same zone the agent's prompt is dated in — so "July 7" means the user's
 * July 7, not UTC's. A date-only value covers the whole day: it snaps to
 * 00:00:00.000 for a start and 23:59:59.999 for an end. Values carrying an
 * offset or trailing "Z" are honoured as absolute instants.
 * @param raw - The `start` / `end` argument as the model sent it.
 * @param edge - Which end of the window is being parsed.
 * @returns Epoch milliseconds, or undefined if absent / unparseable.
 */
function parseBoundary(raw: unknown, edge: 'start' | 'end'): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;

  // Epoch input (seconds or milliseconds) — accepted defensively.
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 1e11 ? raw : raw * 1000;
  }

  const text = String(raw).trim();
  if (!text) return undefined;

  const monthOnly = MONTH_ONLY.exec(text);
  if (monthOnly) {
    const [, y, m] = monthOnly.map(Number);
    return edge === 'start'
      ? new Date(y, m - 1, 1, 0, 0, 0, 0).getTime()
      : // Day 0 of the next month is the last day of this one.
        new Date(y, m, 0, 23, 59, 59, 999).getTime();
  }

  const dateOnly = DATE_ONLY.exec(text);
  if (dateOnly) {
    const [, y, m, d] = dateOnly.map(Number);
    return edge === 'start'
      ? new Date(y, m - 1, d, 0, 0, 0, 0).getTime()
      : new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
  }

  const local = LOCAL_DATETIME.exec(text);
  if (local) {
    const [, y, m, d, hh, mm, ss] = local.map((v) => Number(v || 0));
    return new Date(y, m - 1, d, hh, mm, ss, 0).getTime();
  }

  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Label for a relative lookback.
 * @param hours - Lookback length in hours.
 * @returns A label such as "last 24h" or "last 7 days".
 */
function relativeLabel(hours: number): string {
  if (hours >= 48 && hours % 24 === 0) return `last ${hours / 24} days`;
  return `last ${Math.round(hours * 10) / 10}h`;
}

/**
 * Whether a timestamp sits exactly on local midnight.
 * @param ts - Epoch milliseconds.
 * @returns True if the instant is the first millisecond of a local day.
 */
function isDayStart(ts: number): boolean {
  const d = new Date(ts);
  return (
    d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0
  );
}

/**
 * Whether a timestamp sits on the final second of a local day.
 * @param ts - Epoch milliseconds.
 * @returns True if the instant is 23:59:59 local time.
 */
function isDayEnd(ts: number): boolean {
  const d = new Date(ts);
  return d.getHours() === 23 && d.getMinutes() === 59 && d.getSeconds() === 59;
}

/**
 * Human label for an absolute window — day-granular when it spans whole days,
 * otherwise including the clock times.
 * @param startDate - Window start (epoch ms).
 * @param endDate - Window end (epoch ms).
 * @returns A label such as "Jul 7 – Jul 14, 2026" or "Jul 7, 2026 09:00–17:00".
 */
function absoluteLabel(startDate: number, endDate: number): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dayAligned = isDayStart(startDate) && isDayEnd(endDate);
  const day = (d: Date, withYear: boolean): string =>
    d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {})
    });
  const clock = (d: Date): string =>
    d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const sameDay = start.toDateString() === end.toDateString();

  if (sameDay) {
    return dayAligned
      ? day(start, true)
      : `${day(start, true)} ${clock(start)}–${clock(end)}`;
  }
  const sameYear = start.getFullYear() === end.getFullYear();
  const left = dayAligned ? day(start, !sameYear) : `${day(start, !sameYear)} ${clock(start)}`;
  const right = dayAligned ? day(end, true) : `${day(end, true)} ${clock(end)}`;
  return `${left} – ${right}`;
}

/**
 * Resolve a tool call's time window from either form the model may use:
 * `period_hours` (lookback from now) or `start` / `end` (absolute range).
 *
 * Guarantees a usable, non-future window: a partially specified range is
 * completed, an end past "now" is truncated, and a range that is entirely in
 * the future falls back to the default lookback. Every adjustment is recorded
 * in `notes` so the model can tell the user what it actually charted.
 * @param args - Raw tool arguments from the model.
 * @param defaultHours - Lookback to use when no window is supplied.
 * @returns The resolved window.
 */
function resolveWindow(args: Record<string, unknown>, defaultHours = 24): ResolvedWindow {
  const now = Date.now();
  const notes: string[] = [];
  const rawHours = Number(args.period_hours);
  const hours = Number.isFinite(rawHours) && rawHours > 0 ? rawHours : undefined;

  let startDate = parseBoundary(args.start, 'start');
  let endDate = parseBoundary(args.end, 'end');
  if (args.start !== undefined && args.start !== null && startDate === undefined) {
    notes.push(`Could not read start "${String(args.start)}" — ignored.`);
  }
  if (args.end !== undefined && args.end !== null && endDate === undefined) {
    notes.push(`Could not read end "${String(args.end)}" — ignored.`);
  }

  // Range handed over backwards ("July 14 to July 7"): re-parse with the two
  // inputs exchanged rather than swapping the timestamps, so date-only values
  // still snap outwards and cover both whole days.
  if (startDate !== undefined && endDate !== undefined && startDate > endDate) {
    const flippedStart = parseBoundary(args.end, 'start');
    const flippedEnd = parseBoundary(args.start, 'end');
    if (flippedStart !== undefined && flippedEnd !== undefined && flippedStart <= flippedEnd) {
      startDate = flippedStart;
      endDate = flippedEnd;
      notes.push('start was after end — the two were swapped.');
    }
  }

  if (startDate === undefined && endDate === undefined) {
    const h = hours ?? defaultHours;
    return {
      startDate: now - h * HOUR_MS,
      endDate: now,
      periodHours: h,
      label: relativeLabel(h),
      absolute: false,
      notes
    };
  }

  // Complete a half-open range: an open end runs to now (or `period_hours`
  // forward from the start), an open start runs back from the end.
  if (endDate === undefined) {
    endDate = hours !== undefined ? (startDate as number) + hours * HOUR_MS : now;
  }
  if (startDate === undefined) {
    startDate = endDate - (hours ?? defaultHours) * HOUR_MS;
  }
  // Still inverted (only reachable when one edge was derived from
  // `period_hours`): order the two timestamps as they stand.
  if (startDate > endDate) {
    [startDate, endDate] = [endDate, startDate];
    notes.push('start was after end — the two were swapped.');
  }
  if (startDate >= now) {
    const h = hours ?? defaultHours;
    notes.push(
      `The requested window is entirely in the future — showing the ${relativeLabel(h)} instead.`
    );
    return {
      startDate: now - h * HOUR_MS,
      endDate: now,
      periodHours: h,
      label: relativeLabel(h),
      absolute: false,
      notes
    };
  }
  if (endDate > now) {
    notes.push('The requested end is in the future — the window was truncated at now.');
    endDate = now;
  }

  return {
    startDate,
    endDate,
    periodHours: Math.max(1, Math.round((endDate - startDate) / HOUR_MS)),
    label: absoluteLabel(startDate, endDate),
    absolute: true,
    notes
  };
}

/**
 * The window fields echoed back to the model in every tool result, so it can
 * name the range it charted (and notice when the range was adjusted).
 * @param win - The resolved window.
 * @returns Serializable window description.
 */
function windowEcho(win: ResolvedWindow): Record<string, unknown> {
  return {
    period_hours: win.periodHours,
    window: {
      start: new Date(win.startDate).toISOString(),
      end: new Date(win.endDate).toISOString(),
      label: win.label,
      absolute: win.absolute,
      ...(win.notes.length ? { notes: win.notes } : {})
    }
  };
}

/**
 * Append any window adjustments to a tool's note so the model relays them.
 * @param note - The tool's own note.
 * @param win - The resolved window.
 * @returns The note, with window adjustments appended.
 */
function withWindowNotes(note: string, win: ResolvedWindow): string {
  return win.notes.length ? `${note} ${win.notes.join(' ')}` : note;
}

/**
 * Turn a failed dashboard request into a message the model can pass on.
 *
 * The API rejects windows that fall outside the tenant's plan retention with a
 * 403 and an explanatory body; without unwrapping it the model would only see
 * "Request failed with status code 403" and could not tell the user why.
 * @param err - The rejection from axios.
 * @returns A human-readable failure message.
 */
function describeFetchError(err: unknown): string {
  const response = (err as { response?: { status?: number; data?: any } })?.response;
  const body = response?.data;
  const raw =
    (typeof body === 'string' && body) ||
    body?.message ||
    body?.detailedMessage ||
    body?.error ||
    body?.detail;
  if (!raw) return String((err as Error)?.message || err);

  // The API wraps a rejected range as `403 FORBIDDEN "<reason>"` (Spring's
  // ResponseStatusException, re-serialized by the global handler). Keep the
  // reason and drop the status noise.
  const wrapped = /^\d{3}\s+[A-Z_]+\s+"([\s\S]*)"$/.exec(String(raw).trim());
  const message = wrapped ? wrapped[1] : String(raw);
  if (/plan|older than|historical/i.test(message)) {
    return `The dashboard refused this time range: ${message} Tell the user which window their plan covers and offer a range inside it — do not retry.`;
  }
  return message;
}

/**
 * Fetch per-device time series for a window. Centralises the request payload
 * and error unwrapping shared by every data tool.
 * @param win - Resolved query window.
 * @param sensors - Device serials to scope to; empty for all devices.
 * @returns The response's per-device bucket map (empty object if none).
 */
async function fetchBuckets(
  win: ResolvedWindow,
  sensors: number[] = []
): Promise<Record<string, any>> {
  try {
    const resp = await getDevicesSummaryOverTime({
      startDate: win.startDate,
      endDate: win.endDate,
      clients: [],
      zones: [],
      rooms: [],
      facility: [],
      sensors,
      sensorNumber: [],
      devices: sensors
    } as any);
    return resp?.data?.data || {};
  } catch (err) {
    throw new Error(describeFetchError(err));
  }
}

/**
 * Pick the friendliest available label for a device. The dashboard sometimes
 * stores a serial-number string in `deviceName` for unnamed devices, so prefer
 * fields that look human-written (purpose, then the first tag, then
 * deviceName if it isn't just digits), falling back to "Device <serial>".
 * @param dev - The device record, or undefined if it could not be resolved.
 * @param deviceKey - The bucket key (typically the serial number) for fallback.
 * @returns A human-readable device label.
 */
function deviceLabel(
  dev: DeviceAttributes | undefined,
  deviceKey: string | number
): string {
  const looksLikeName = (s: unknown): s is string =>
    typeof s === 'string' && s.trim().length > 0 && !/^\d+$/.test(s.trim());
  if (dev) {
    const candidates: unknown[] = [
      dev.purpose,
      Array.isArray(dev.tags) ? dev.tags[0] : undefined,
      dev.deviceName
    ];
    const friendly = candidates.find(looksLikeName);
    if (typeof friendly === 'string') return friendly.trim();
    return `Device ${dev.serialNumber}`;
  }
  return `Device ${deviceKey}`;
}

/**
 * Whether a device passes a `device_type` filter ('INDOOR' / 'OUTDOOR').
 * Returns true when no filter is set; case-insensitive otherwise.
 * @param dev - Device record (or undefined if the bucket key did not resolve).
 * @param filter - The model-supplied filter argument, if any.
 * @returns Whether to include the device in the result.
 */
function matchesDeviceType(
  dev: DeviceAttributes | undefined,
  filter: unknown
): boolean {
  if (!filter || typeof filter !== 'string') return true;
  if (!dev) return false;
  return String(dev.deviceType || '').toUpperCase() === filter.toUpperCase();
}

/** Which devices a tool call is limited to, resolved from serials and names. */
interface DeviceScope {
  /** Serial allow-list. Empty means "every device". */
  serials: number[];
  /** Human labels of the matched devices, echoed to the model. */
  matched: string[];
  /** Requested names that matched no device, echoed to the model. */
  unmatched: string[];
}

/**
 * Strip case and punctuation so "VERL-CVS" matches "verl cvs".
 * @param value - Any device name / tag / serial.
 * @returns The comparable form (lower-case alphanumerics only).
 */
function normalizeDeviceName(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Every string a device can be referred to by.
 * @param dev - The device record.
 * @returns Normalized aliases (name, purpose, tags, serial).
 */
function deviceAliases(dev: DeviceAttributes): string[] {
  const tags = Array.isArray(dev.tags) ? dev.tags : [];
  return [dev.deviceName, dev.purpose, ...tags, dev.serialNumber]
    .map(normalizeDeviceName)
    .filter(Boolean);
}

/**
 * Resolve the devices a call is scoped to from `device_serials` and/or
 * `device_names`.
 *
 * Names are matched exactly first, then by prefix/substring, so "VERL1" finds
 * "VERL1" rather than also dragging in "VERL2738C" whenever an exact hit
 * exists. Anything unmatched is reported instead of being silently dropped —
 * a request for three named devices must never quietly become "all devices".
 * @param args - Raw tool arguments from the model.
 * @param devices - All devices available to the tenant.
 * @returns The resolved scope.
 */
function resolveDeviceScope(
  args: Record<string, unknown>,
  devices: DeviceAttributes[]
): DeviceScope {
  const serials: number[] = [];
  const matched: string[] = [];
  const unmatched: string[] = [];

  const addSerial = (serial: unknown, label: string): void => {
    const n = Number(serial);
    if (!Number.isFinite(n) || serials.includes(n)) return;
    serials.push(n);
    matched.push(label);
  };

  const rawSerials = Array.isArray(args.device_serials) ? args.device_serials : [];
  rawSerials.forEach((s) => {
    const dev = devices.find((d) => String(d.serialNumber) === String(s));
    addSerial(s, deviceLabel(dev, String(s)));
  });

  const rawNames = Array.isArray(args.device_names) ? args.device_names : [];
  rawNames.forEach((raw) => {
    const needle = normalizeDeviceName(raw);
    if (!needle) return;
    const exact = devices.filter((d) => deviceAliases(d).includes(needle));
    const hits = exact.length
      ? exact
      : devices.filter((d) =>
          deviceAliases(d).some((a) => a.startsWith(needle) || a.includes(needle))
        );
    if (!hits.length) {
      unmatched.push(String(raw));
      return;
    }
    hits.forEach((d) => addSerial(d.serialNumber, deviceLabel(d, d.serialNumber)));
  });

  return { serials, matched, unmatched };
}

/**
 * Build the per-device include predicate for one tool call: a device must pass
 * the `device_type` filter and, when the call named devices, be one of them.
 * @param args - Raw tool arguments (read for `device_type`).
 * @param scope - Resolved device scope.
 * @returns Predicate over (device record, bucket key).
 */
function deviceGate(
  args: Record<string, unknown>,
  scope: DeviceScope
): (dev: DeviceAttributes | undefined, deviceKey: string | number) => boolean {
  const allowed = new Set(scope.serials.map(String));
  return (dev, deviceKey) => {
    if (!matchesDeviceType(dev, args.device_type)) return false;
    if (allowed.size && !allowed.has(String(deviceKey))) return false;
    return true;
  };
}

/**
 * The device scope echoed back to the model alongside the window.
 * @param scope - Resolved device scope.
 * @returns The echo object, or undefined when the call was not scoped.
 */
function deviceEcho(scope: DeviceScope): Record<string, unknown> | undefined {
  if (!scope.serials.length && !scope.unmatched.length) return undefined;
  return {
    devices: {
      matched: scope.matched,
      serials: scope.serials,
      ...(scope.unmatched.length ? { unmatched: scope.unmatched } : {})
    }
  };
}

/**
 * Append an unmatched-device warning to a tool note so the model relays it.
 * @param note - The tool's own note.
 * @param scope - Resolved device scope.
 * @returns The note, with any unmatched device names appended.
 */
function withScopeNotes(note: string, scope: DeviceScope): string {
  if (!scope.unmatched.length) return note;
  return `${note} No device matched: ${scope.unmatched.join(', ')} — tell the user and offer the available device names.`;
}

/**
 * Compute the qth quantile of a pre-sorted ascending array using linear
 * interpolation between adjacent ranks (the R-7 / Excel definition).
 * @param sorted - Values sorted ascending.
 * @param q - Quantile in [0, 1].
 * @returns The quantile value, or 0 for an empty input.
 */
function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return 0;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

function basicStats(values: number[]): {
  min: number;
  max: number;
  avg: number;
  first: number;
  last: number;
  changePct: number;
} {
  const clean = values.filter((v) => Number.isFinite(v) && v !== 0);
  if (!clean.length) return { min: 0, max: 0, avg: 0, first: 0, last: 0, changePct: 0 };
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const avg = clean.reduce((a, b) => a + b, 0) / clean.length;
  const first = clean[0];
  const last = clean[clean.length - 1];
  const changePct = first ? ((last - first) / Math.abs(first)) * 100 : 0;
  return { min, max, avg, first, last, changePct };
}

/**
 * Upper-case the first letter — used for window labels in chart titles.
 * @param text - The text to capitalize.
 * @returns The text with its first character upper-cased.
 */
function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

function round(n: number, d = 2): number {
  if (!Number.isFinite(n)) return 0;
  const f = 10 ** d;
  return Math.round(n * f) / f;
}

function pickRenderNote(normalized: boolean, dualAxis: boolean): string {
  if (normalized) {
    return 'Multi-parameter chart rendered with min/max normalization (0–100). Tooltip and stats keep original units.';
  }
  if (dualAxis) {
    return 'Dual-axis chart rendered (left + right Y axes for the two units).';
  }
  return 'Line chart rendered.';
}

function trendOf(changePct: number): MetricCard['trend'] {
  if (Math.abs(changePct) < 1) return { direction: 'flat', pct: round(changePct, 1) };
  return {
    direction: changePct > 0 ? 'up' : 'down',
    pct: round(Math.abs(changePct), 1)
  };
}

/**
 * Build the client-side tool executors, bound to the current data context snapshot.
 * @param access - Live snapshot of data-context state (devices, latest summaries) the tools read from.
 * @returns Map of tool name -> executor; the agent page runs these on each model tool call.
 */
export function buildAgentTools(access: AgentDataAccess): AgentToolExecutors {
  /**
   * Resolve a response bucket key (the serial number) to its device record.
   * @param deviceKey - Bucket key from the summary response.
   * @returns The matching device, or undefined if it is unknown.
   */
  const findDevice = (deviceKey: string | number): DeviceAttributes | undefined =>
    access.devices.find((d) => String(d.serialNumber) === String(deviceKey));

  const executors: AgentToolExecutors = {
    async list_capabilities(): Promise<ToolResult> {
      const indoorCount = access.devices.filter(
        (d) => String(d.deviceType || '').toUpperCase() === 'INDOOR'
      ).length;
      const outdoorCount = access.devices.filter(
        (d) => String(d.deviceType || '').toUpperCase() === 'OUTDOOR'
      ).length;
      const historyDays = access.historyDays ?? null;
      const earliest =
        historyDays && historyDays > 0
          ? new Date(Date.now() - historyDays * DAY_MS).toISOString().slice(0, 10)
          : null;
      return {
        data: {
          parameters: SUPPORTED_PARAMS.map((p) => ({
            code: p,
            name: PRETTY_NAME[p],
            unit: paramUnit(p)
          })),
          devices: access.devices.slice(0, 60).map((d) => ({
            serial: d.serialNumber,
            name: deviceLabel(d, d.serialNumber),
            device_type: d.deviceType || null
          })),
          device_count: access.devices.length,
          indoor_count: indoorCount,
          outdoor_count: outdoorCount,
          /**
           * The `device_type` arg accepted by every data tool. The model
           * should pass exactly one of these strings to scope a query.
           */
          device_type_values: ['INDOOR', 'OUTDOOR'],
          /** Days of history the plan allows; null = unlimited. */
          history_days: historyDays,
          /** Oldest queryable date, when the plan limits history. */
          earliest_queryable_date: earliest
        },
        note: `Tenant has ${access.devices.length} devices — ${indoorCount} indoor, ${outdoorCount} outdoor. Pass device_type:"INDOOR" or "OUTDOOR" on any data tool to scope a query, or device_names:[…] to scope to devices the user named. ${
          earliest
            ? `History is limited to ${historyDays} days by the plan — the oldest queryable date is ${earliest}.`
            : 'History depth is unlimited on this plan.'
        }`
      };
    },

    async get_current_snapshot(): Promise<ToolResult> {
      const params = SUPPORTED_PARAMS.filter((p) => Number.isFinite(access.dataSummary?.[p]));
      const cards: MetricCard[] = params.slice(0, 6).map((p) => ({
        label: PRETTY_NAME[p],
        value: round(access.dataSummary[p], 2).toString(),
        unit: paramUnit(p),
        tone: 'neutral'
      }));
      return {
        data: {
          values: params.reduce<Record<string, number>>((acc, p) => {
            acc[p] = round(access.dataSummary[p], 3);
            return acc;
          }, {}),
          device_count: access.devices.length
        },
        display: cards.length ? [{ kind: 'metrics', title: 'Right now', cards }] : undefined,
        note: 'Latest reading per parameter across all devices.'
      };
    },

    async summarize_period(args): Promise<ToolResult> {
      const params = (args.parameters as string[]) || [];
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const buckets = await fetchBuckets(win, scope.serials);
      const out: Record<string, ReturnType<typeof basicStats> & { peak_time?: string }> = {};
      params.forEach((p) => {
        const all: number[] = [];
        let peakValue = -Infinity;
        let peakTs = 0;
        Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
          const dev = findDevice(deviceKey);
          if (!include(dev, deviceKey)) return;
          (deviceArr || []).forEach((series: any) => {
            if (series.name !== p) return;
            (series.data || []).forEach(([ts, v]: [number, number]) => {
              if (!Number.isFinite(v) || v === 0) return;
              all.push(v);
              if (v > peakValue) {
                peakValue = v;
                peakTs = ts;
              }
            });
          });
        });
        const stats = basicStats(all);
        out[p] = {
          ...stats,
          min: round(stats.min, 2),
          max: round(stats.max, 2),
          avg: round(stats.avg, 2),
          first: round(stats.first, 2),
          last: round(stats.last, 2),
          changePct: round(stats.changePct, 1),
          peak_time: peakTs ? new Date(peakTs).toISOString() : undefined
        };
      });
      return {
        data: { ...windowEcho(win), ...deviceEcho(scope), stats: out },
        note: withScopeNotes(withWindowNotes(`Stats for ${win.label}.`, win), scope)
      };
    },

    async render_line_chart(args): Promise<ToolResult> {
      // Accept both `parameters: string[]` (preferred) and legacy `parameter: string`.
      const rawParams = (args.parameters as string[]) || [];
      const legacyParam = args.parameter ? [String(args.parameter)] : [];
      const params = (rawParams.length ? rawParams : legacyParam).filter(Boolean);
      if (!params.length) {
        return { data: { error: 'No parameter(s) provided.' } };
      }
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const subtitle = args.subtitle ? String(args.subtitle) : undefined;

      const defaultTitle =
        params.length === 1
          ? `${PRETTY_NAME[params[0]] || params[0]} over time (${win.label})`
          : `${params.map((p) => PRETTY_NAME[p] || p).join(' vs ')} (${win.label})`;
      const title = String(args.title || defaultTitle);

      const buckets = await fetchBuckets(win, scope.serials);

      // Bucket a single device's samples of one parameter into 5-min slots.
      // Unlike aggregatePoints below, this keeps the device separate instead
      // of averaging it in with every other device.
      const deviceBucketPoints = (
        parameter: string,
        deviceKey: string
      ): [number, number][] => {
        const tsMap = new Map<number, { sum: number; n: number }>();
        ((buckets as Record<string, any>)[deviceKey] || []).forEach((s: any) => {
          if (s.name !== parameter) return;
          (s.data || []).forEach(([ts, v]: [number, number]) => {
            if (!Number.isFinite(v) || v === 0) return;
            const slot = Math.round(ts / (5 * 60 * 1000)) * 5 * 60 * 1000;
            const cur = tsMap.get(slot) || { sum: 0, n: 0 };
            cur.sum += v;
            cur.n += 1;
            tsMap.set(slot, cur);
          });
        });
        return Array.from(tsMap.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([ts, { sum, n }]) => [ts, sum / n]);
      };

      // Per-device mode: draw one line per device (not averaged together) so
      // the user can compare the same metric across rooms/sensors on one chart.
      // Triggered either by split_by_device (the model's intent flag, no scope
      // needed) or by scoping the call to 2+ devices. When the call names no
      // devices we enumerate every device present in the response. Falls back
      // to the averaged path if fewer than two devices actually have data.
      const wantPerDevice =
        params.length === 1 && (args.split_by_device === true || scope.serials.length >= 2);
      if (wantPerDevice) {
        const param = params[0];
        const unit = paramUnit(param);
        // Which devices become lines: the scoped devices if 2+ were named,
        // otherwise every device key returned in the buckets.
        const deviceKeys: string[] =
          scope.serials.length >= 2 ? scope.serials.map(String) : Object.keys(buckets);
        const deviceSeries: ChartSeries[] = deviceKeys
          .map((key, i) => {
            const dev = findDevice(key);
            if (!include(dev, key)) return null;
            const points = deviceBucketPoints(param, key);
            if (!points.length) return null;
            return {
              name: deviceLabel(dev, key),
              color: DEVICE_LINE_COLORS[i % DEVICE_LINE_COLORS.length],
              unit,
              points
            } as ChartSeries;
          })
          .filter((s): s is ChartSeries => s !== null);

        if (deviceSeries.length >= 2) {
          const block: RenderBlock = {
            kind: 'line-chart',
            title,
            subtitle,
            series: deviceSeries,
            unit,
            normalized: false,
            area: false
          };
          const statsByDevice = deviceSeries.reduce<Record<string, unknown>>(
            (acc, s) => {
              const st = basicStats(s.points.map(([, v]) => v));
              acc[s.name] = {
                unit,
                min: round(st.min, 2),
                max: round(st.max, 2),
                avg: round(st.avg, 2),
                change_pct: round(st.changePct, 1)
              };
              return acc;
            },
            {}
          );
          return {
            data: {
              parameter: param,
              ...windowEcho(win),
              ...deviceEcho(scope),
              per_device: true,
              stats: statsByDevice,
              point_counts: deviceSeries.map((s) => ({
                device: s.name,
                n: s.points.length
              }))
            },
            display: [block],
            note: withScopeNotes(
              withWindowNotes(
                `Rendered one line per device (${deviceSeries.length}) for ${
                  PRETTY_NAME[param] || param
                } over ${win.label}.`,
                win
              ),
              scope
            )
          };
        }
        // Only one device had data — fall through to the averaged single line.
      }

      // For each parameter, average across all devices into 5-min buckets.
      const aggregatePoints = (parameter: string): [number, number][] => {
        const tsMap = new Map<number, { sum: number; n: number }>();
        Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
          const dev = findDevice(deviceKey);
          if (!include(dev, deviceKey)) return;
          (deviceArr || []).forEach((s: any) => {
            if (s.name !== parameter) return;
            (s.data || []).forEach(([ts, v]: [number, number]) => {
              if (!Number.isFinite(v) || v === 0) return;
              const slot = Math.round(ts / (5 * 60 * 1000)) * 5 * 60 * 1000;
              const cur = tsMap.get(slot) || { sum: 0, n: 0 };
              cur.sum += v;
              cur.n += 1;
              tsMap.set(slot, cur);
            });
          });
        });
        return Array.from(tsMap.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([ts, { sum, n }]) => [ts, sum / n]);
      };

      const rawSeries: { param: string; unit: string; points: [number, number][] }[] = params
        .map((p) => ({ param: p, unit: paramUnit(p), points: aggregatePoints(p) }))
        .filter((s) => s.points.length > 0);

      if (!rawSeries.length) {
        return {
          data: {
            error: `No data points found for ${win.label}.`,
            params,
            ...windowEcho(win),
            ...deviceEcho(scope)
          }
        };
      }

      const uniqueUnits = Array.from(new Set(rawSeries.map((s) => s.unit)));
      let finalSeries: ChartSeries[];
      let blockUnit: string | undefined;
      let rightUnit: string | undefined;
      let normalized = false;

      if (rawSeries.length === 1) {
        // Single parameter: simple, original units.
        const s = rawSeries[0];
        finalSeries = [
          {
            name: PRETTY_NAME[s.param] || s.param,
            color: paramColor(s.param),
            unit: s.unit,
            points: s.points
          }
        ];
        blockUnit = s.unit;
      } else if (rawSeries.length === 2 && uniqueUnits.length > 1) {
        // Two parameters with different units → dual-axis.
        finalSeries = rawSeries.map((s, i) => ({
          name: PRETTY_NAME[s.param] || s.param,
          color: paramColor(s.param),
          unit: s.unit,
          axis: i === 0 ? ('left' as const) : ('right' as const),
          points: s.points
        }));
        blockUnit = rawSeries[0].unit;
        rightUnit = rawSeries[1].unit;
      } else if (uniqueUnits.length === 1) {
        // All same units → just plot together.
        finalSeries = rawSeries.map((s) => ({
          name: PRETTY_NAME[s.param] || s.param,
          color: paramColor(s.param),
          unit: s.unit,
          points: s.points
        }));
        [blockUnit] = uniqueUnits;
      } else {
        // 3+ series with mixed units → min-max normalize each to [0, 100].
        normalized = true;
        finalSeries = rawSeries.map((s) => {
          const vals = s.points.map(([, v]) => v);
          const min = Math.min(...vals);
          const max = Math.max(...vals);
          const range01 = max - min || 1;
          return {
            name: `${PRETTY_NAME[s.param] || s.param}${s.unit ? ` (${s.unit})` : ''}`,
            color: paramColor(s.param),
            unit: s.unit,
            scale: range01 / 100,
            offset: min,
            points: s.points.map(([ts, v]) => [ts, ((v - min) / range01) * 100] as [number, number])
          };
        });
      }

      const block: RenderBlock = {
        kind: 'line-chart',
        title,
        subtitle,
        series: finalSeries,
        unit: blockUnit,
        rightUnit,
        normalized,
        area: String(args.style) === 'area'
      };

      const statsByParam = rawSeries.reduce<Record<string, unknown>>((acc, s) => {
        const stats = basicStats(s.points.map(([, v]) => v));
        acc[s.param] = {
          unit: s.unit,
          min: round(stats.min, 2),
          max: round(stats.max, 2),
          avg: round(stats.avg, 2),
          change_pct: round(stats.changePct, 1)
        };
        return acc;
      }, {});

      return {
        data: {
          parameters: params,
          ...windowEcho(win),
          ...deviceEcho(scope),
          stats: statsByParam,
          point_counts: rawSeries.map((s) => ({ param: s.param, n: s.points.length })),
          dual_axis: !!rightUnit,
          normalized
        },
        display: [block],
        note: withScopeNotes(
          withWindowNotes(`${pickRenderNote(normalized, !!rightUnit)} Window: ${win.label}.`, win),
          scope
        )
      };
    },

    async render_metric_cards(args): Promise<ToolResult> {
      const params = (args.parameters as string[]) || [];
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const title = (args.title as string) || capitalize(win.label);
      const buckets = await fetchBuckets(win, scope.serials);

      const cards: MetricCard[] = params.map((p) => {
        const values: number[] = [];
        Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
          const dev = findDevice(deviceKey);
          if (!include(dev, deviceKey)) return;
          (deviceArr || []).forEach((s: any) => {
            if (s.name !== p) return;
            (s.data || []).forEach(([, v]: [number, number]) => {
              if (Number.isFinite(v) && v !== 0) values.push(v);
            });
          });
        });
        const stats = basicStats(values);
        return {
          label: PRETTY_NAME[p] || p,
          value: round(stats.last || stats.avg, 2).toString(),
          unit: paramUnit(p),
          trend: trendOf(stats.changePct),
          tone: 'neutral'
        };
      });

      return {
        data: { cards: cards.map((c) => ({ ...c })), ...windowEcho(win), ...deviceEcho(scope) },
        display: [{ kind: 'metrics', title, cards }],
        note: withScopeNotes(withWindowNotes(`Metric cards rendered for ${win.label}.`, win), scope)
      };
    },

    async render_callout(args): Promise<ToolResult> {
      const block: RenderBlock = {
        kind: 'callout',
        tone: (args.tone as 'info' | 'warn' | 'good') || 'info',
        title: String(args.title),
        body: String(args.body)
      };
      return { data: { ok: true }, display: [block] };
    },

    async compare_devices(args): Promise<ToolResult> {
      const rawParams = (args.parameters as string[]) || [];
      const legacyParam = args.parameter ? [String(args.parameter)] : [];
      const params = (rawParams.length ? rawParams : legacyParam).filter(Boolean);
      if (!params.length) {
        return { data: { error: 'No parameter(s) provided.' } };
      }
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const topN = Number(args.top_n) || 8;
      const buckets = await fetchBuckets(win, scope.serials);

      // perDevice[deviceKey][param] = avg
      const perDevice: Record<string, { name: string; avgs: Record<string, number> }> = {};
      Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
        const dev = findDevice(deviceKey);
        if (!include(dev, deviceKey)) return;
        const name = deviceLabel(dev, deviceKey);
        const avgs: Record<string, number> = {};
        (deviceArr || []).forEach((s: any) => {
          if (!params.includes(s.name)) return;
          const vals = (s.data || [])
            .map(([, v]: [number, number]) => v)
            .filter((v: number) => Number.isFinite(v) && v !== 0);
          if (!vals.length) return;
          avgs[s.name] = vals.reduce((a: number, b: number) => a + b, 0) / vals.length;
        });
        if (Object.keys(avgs).length > 0) {
          perDevice[deviceKey] = { name, avgs };
        }
      });

      // Sort devices by the FIRST parameter's average (desc), missing values to the end.
      const primary = params[0];
      const sortedDevices = Object.values(perDevice).sort(
        (a, b) => (b.avgs[primary] ?? -Infinity) - (a.avgs[primary] ?? -Infinity)
      );
      const top = sortedDevices.slice(0, topN);
      if (!top.length) {
        return {
          data: {
            error: `No data points found for ${win.label}.`,
            params,
            ...windowEcho(win),
            ...deviceEcho(scope)
          }
        };
      }

      const categories = top.map((d) => d.name);
      const uniqueUnits = Array.from(new Set(params.map((p) => paramUnit(p))));
      const normalize = params.length > 1 && uniqueUnits.length > 1;

      const series = params.map((p) => {
        const rawValues = top.map((d) => d.avgs[p] ?? 0);
        if (!normalize) {
          return {
            name: PRETTY_NAME[p] || p,
            color: paramColor(p),
            unit: paramUnit(p),
            values: rawValues.map((v) => round(v, 2))
          };
        }
        const min = Math.min(...rawValues.filter((v) => Number.isFinite(v) && v !== 0));
        const max = Math.max(...rawValues);
        const span = max - min || 1;
        return {
          name: `${PRETTY_NAME[p] || p}${paramUnit(p) ? ` (${paramUnit(p)})` : ''}`,
          color: paramColor(p),
          unit: paramUnit(p),
          scale: span / 100,
          offset: min,
          values: rawValues.map((v) => round(((v - min) / span) * 100, 2))
        };
      });

      const titleSuffix = `${params.map((p) => PRETTY_NAME[p] || p).join(' & ')} — per device (${win.label})`;
      const orientation: 'horizontal' | 'vertical' =
        args.orientation === 'vertical' ? 'vertical' : 'horizontal';
      const block: RenderBlock = {
        kind: 'bar-chart',
        title: String(args.title || titleSuffix),
        categories,
        series,
        unit: !normalize && uniqueUnits.length === 1 ? uniqueUnits[0] : undefined,
        normalized: normalize,
        orientation
      };

      return {
        data: {
          parameters: params,
          ...windowEcho(win),
          ...deviceEcho(scope),
          orientation,
          devices: top.map((d) => ({
            name: d.name,
            avgs: Object.fromEntries(
              Object.entries(d.avgs).map(([k, v]) => [k, round(v, 2)])
            )
          })),
          normalized: normalize
        },
        display: [block],
        note: withScopeNotes(
          withWindowNotes(
            normalize
              ? `Multi-parameter bar chart rendered for ${win.label} with min/max normalization (0–100). Tooltip recovers original units.`
              : `Bar chart rendered for ${win.label}.`,
            win
          ),
          scope
        )
      };
    },

    async render_scatter(args): Promise<ToolResult> {
      const xParam = String(args.x_parameter || '');
      const yParam = String(args.y_parameter || '');
      if (!xParam || !yParam) {
        return { data: { error: 'x_parameter and y_parameter are both required.' } };
      }
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const buckets = await fetchBuckets(win, scope.serials);

      // Average a parameter across all devices into 5-min time slots.
      const slotAverages = (parameter: string): Map<number, number> => {
        const tmp = new Map<number, { sum: number; n: number }>();
        Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
          const dev = findDevice(deviceKey);
          if (!include(dev, deviceKey)) return;
          (deviceArr || []).forEach((s: any) => {
            if (s.name !== parameter) return;
            (s.data || []).forEach(([ts, v]: [number, number]) => {
              if (!Number.isFinite(v) || v === 0) return;
              const slot = Math.round(ts / (5 * 60 * 1000)) * 5 * 60 * 1000;
              const cur = tmp.get(slot) || { sum: 0, n: 0 };
              cur.sum += v;
              cur.n += 1;
              tmp.set(slot, cur);
            });
          });
        });
        const out = new Map<number, number>();
        tmp.forEach((v, k) => out.set(k, v.sum / v.n));
        return out;
      };

      const xMap = slotAverages(xParam);
      const yMap = slotAverages(yParam);
      const points: { x: number; y: number }[] = [];
      xMap.forEach((xv, slot) => {
        const yv = yMap.get(slot);
        if (yv !== undefined) points.push({ x: round(xv, 3), y: round(yv, 3) });
      });
      if (!points.length) {
        return {
          data: {
            error: `No overlapping data points for ${win.label}.`,
            ...windowEcho(win),
            ...deviceEcho(scope)
          }
        };
      }
      points.sort((a, b) => a.x - b.x);

      // Pearson correlation coefficient.
      const n = points.length;
      const mx = points.reduce((a, p) => a + p.x, 0) / n;
      const my = points.reduce((a, p) => a + p.y, 0) / n;
      let cov = 0;
      let vx = 0;
      let vy = 0;
      points.forEach((p) => {
        cov += (p.x - mx) * (p.y - my);
        vx += (p.x - mx) ** 2;
        vy += (p.y - my) ** 2;
      });
      const r = vx && vy ? cov / Math.sqrt(vx * vy) : 0;
      let strength = 'weak';
      if (Math.abs(r) > 0.7) {
        strength = 'strong';
      } else if (Math.abs(r) > 0.4) {
        strength = 'moderate';
      }

      const block: RenderBlock = {
        kind: 'scatter-chart',
        title: String(
          args.title || `${PRETTY_NAME[yParam] || yParam} vs ${PRETTY_NAME[xParam] || xParam}`
        ),
        subtitle: args.subtitle ? String(args.subtitle) : undefined,
        xLabel: PRETTY_NAME[xParam] || xParam,
        yLabel: PRETTY_NAME[yParam] || yParam,
        xUnit: paramUnit(xParam),
        yUnit: paramUnit(yParam),
        color: paramColor(yParam),
        points
      };

      return {
        data: {
          x_parameter: xParam,
          y_parameter: yParam,
          ...windowEcho(win),
          ...deviceEcho(scope),
          point_count: n,
          correlation: round(r, 3)
        },
        display: [block],
        note: withScopeNotes(
          withWindowNotes(
            `Scatter chart rendered for ${win.label}. Pearson r = ${round(r, 3)} (${strength} correlation).`,
            win
          ),
          scope
        )
      };
    },

    async render_donut(args): Promise<ToolResult> {
      const param = String(args.parameter || '');
      if (!param) {
        return { data: { error: 'parameter is required.' } };
      }
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const topN = Number(args.top_n) || 8;
      const buckets = await fetchBuckets(win, scope.serials);

      const perDevice: { name: string; avg: number }[] = [];
      Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
        const dev = findDevice(deviceKey);
        if (!include(dev, deviceKey)) return;
        const name = deviceLabel(dev, deviceKey);
        (deviceArr || []).forEach((s: any) => {
          if (s.name !== param) return;
          const vals = (s.data || [])
            .map(([, v]: [number, number]) => v)
            .filter((v: number) => Number.isFinite(v) && v !== 0);
          if (vals.length) {
            perDevice.push({
              name,
              avg: vals.reduce((a: number, b: number) => a + b, 0) / vals.length
            });
          }
        });
      });
      if (!perDevice.length) {
        return {
          data: {
            error: `No data found for ${win.label}.`,
            ...windowEcho(win),
            ...deviceEcho(scope)
          }
        };
      }
      perDevice.sort((a, b) => b.avg - a.avg);
      const segments = perDevice
        .slice(0, topN)
        .map((d) => ({ label: d.name, value: round(d.avg, 2) }));

      const block: RenderBlock = {
        kind: 'donut-chart',
        title: String(
          args.title || `${PRETTY_NAME[param] || param} share by device (${win.label})`
        ),
        unit: paramUnit(param),
        segments
      };

      return {
        data: { parameter: param, ...windowEcho(win), ...deviceEcho(scope), devices: segments },
        display: [block],
        note: withScopeNotes(
          withWindowNotes(
            `Donut chart rendered (per-device average share) for ${win.label}.`,
            win
          ),
          scope
        )
      };
    },

    async render_gauge(args): Promise<ToolResult> {
      const param = String(args.parameter || '');
      if (!param) {
        return { data: { error: 'parameter is required.' } };
      }
      const cfg = GAUGE_CONFIG[param] || { min: 0, max: 100, zones: [] };
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      // A window in either form (period_hours or start/end) gauges that
      // window's average; with no window at all we gauge the live reading.
      const hasWindow = ['period_hours', 'start', 'end'].some(
        (k) => args[k] !== undefined && args[k] !== null && args[k] !== ''
      );

      let value: number;
      let basis: string;
      let win: ResolvedWindow | undefined;
      if (hasWindow) {
        win = resolveWindow(args);
        const buckets = await fetchBuckets(win, scope.serials);
        const vals: number[] = [];
        Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
          const dev = findDevice(deviceKey);
          if (!include(dev, deviceKey)) return;
          (deviceArr || []).forEach((s: any) => {
            if (s.name !== param) return;
            (s.data || []).forEach(([, v]: [number, number]) => {
              if (Number.isFinite(v) && v !== 0) vals.push(v);
            });
          });
        });
        if (!vals.length) {
          return {
            data: {
              error: `No data found for ${win.label}.`,
              ...windowEcho(win),
              ...deviceEcho(scope)
            }
          };
        }
        value = vals.reduce((a, b) => a + b, 0) / vals.length;
        basis = `${win.label} average`;
      } else if (scope.serials.length) {
        // Scoped live reading: average the latest per-device values for the
        // named devices rather than the tenant-wide snapshot, which would
        // silently answer for every sensor.
        const allowed = new Set(scope.serials.map(String));
        const perDevice = Object.entries(access.deviceDataSummary || {})
          .filter(([key, entry]) => allowed.has(String(key)) || allowed.has(String(entry?.device)))
          .map(([, entry]) => Number(entry?.parameters?.[param as ParametersType]))
          .filter((v) => Number.isFinite(v) && v !== 0);
        if (!perDevice.length) {
          return {
            data: {
              error: `No current ${param} reading for ${scope.matched.join(', ') || 'those devices'}. Ask for a time window instead, or check the device names.`,
              ...deviceEcho(scope)
            }
          };
        }
        value = perDevice.reduce((a, b) => a + b, 0) / perDevice.length;
        basis =
          perDevice.length > 1
            ? `current reading, averaged over ${perDevice.length} devices`
            : 'current reading';
      } else {
        const live = access.dataSummary?.[param as ParametersType];
        if (!Number.isFinite(live)) {
          return { data: { error: `No current reading available for ${param}.` } };
        }
        value = live as number;
        basis = 'current reading';
      }

      // Stretch the dial if the value runs past the configured max.
      const max = Math.max(cfg.max, value * 1.1);
      const block: RenderBlock = {
        kind: 'gauge',
        title: String(args.title || `${PRETTY_NAME[param] || param} — ${basis}`),
        value: round(value, 2),
        min: cfg.min,
        max: round(max, 0),
        unit: paramUnit(param),
        zones: cfg.zones.length ? cfg.zones : undefined
      };

      return {
        data: {
          parameter: param,
          value: round(value, 2),
          min: cfg.min,
          max: round(max, 0),
          basis,
          ...(win ? windowEcho(win) : {}),
          ...deviceEcho(scope)
        },
        display: [block],
        note: withScopeNotes(
          win ? withWindowNotes(`Gauge rendered (${basis}).`, win) : `Gauge rendered (${basis}).`,
          scope
        )
      };
    },

    async render_box_plot(args): Promise<ToolResult> {
      const param = String(args.parameter || '');
      if (!param) return { data: { error: 'parameter is required.' } };
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const topN = Number(args.top_n) || 8;
      const buckets = await fetchBuckets(win, scope.serials);

      const perDevice: {
        name: string;
        stats: ReturnType<typeof basicStats> & {
          q1: number;
          q3: number;
          median: number;
        };
      }[] = [];
      Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
        const dev = findDevice(deviceKey);
        if (!include(dev, deviceKey)) return;
        const name = deviceLabel(dev, deviceKey);
        (deviceArr || []).forEach((s: any) => {
          if (s.name !== param) return;
          const vals = (s.data || [])
            .map(([, v]: [number, number]) => v)
            .filter((v: number) => Number.isFinite(v) && v !== 0) as number[];
          if (vals.length < 4) return;
          const sorted = [...vals].sort((a, b) => a - b);
          const stats = basicStats(vals);
          perDevice.push({
            name,
            stats: {
              ...stats,
              q1: quantile(sorted, 0.25),
              median: quantile(sorted, 0.5),
              q3: quantile(sorted, 0.75)
            }
          });
        });
      });
      if (!perDevice.length) {
        return {
          data: {
            error: `Not enough data for a box plot over ${win.label}.`,
            ...windowEcho(win),
            ...deviceEcho(scope)
          }
        };
      }
      perDevice.sort((a, b) => b.stats.median - a.stats.median);
      const top = perDevice.slice(0, topN);
      const boxStats = top.map((d) => ({
        category: d.name,
        min: round(d.stats.min, 2),
        q1: round(d.stats.q1, 2),
        median: round(d.stats.median, 2),
        q3: round(d.stats.q3, 2),
        max: round(d.stats.max, 2),
        mean: round(d.stats.avg, 2)
      }));

      const block: RenderBlock = {
        kind: 'box-plot',
        title: String(
          args.title || `${PRETTY_NAME[param] || param} distribution by device (${win.label})`
        ),
        subtitle: args.subtitle ? String(args.subtitle) : undefined,
        stats: boxStats,
        unit: paramUnit(param),
        color: paramColor(param)
      };

      return {
        data: {
          parameter: param,
          ...windowEcho(win),
          ...deviceEcho(scope),
          stats: boxStats
        },
        display: [block],
        note: withScopeNotes(
          withWindowNotes(
            `Box plot rendered for ${win.label} (min / Q1 / median / Q3 / max per device).`,
            win
          ),
          scope
        )
      };
    },

    async render_health_impact(args): Promise<ToolResult> {
      const scores = ((args.scores as any[]) || [])
        .filter((s) => s && typeof s.label === 'string')
        .map((s) => ({
          label: String(s.label),
          basis: String(s.basis || ''),
          impactPct: Math.max(-100, Math.min(100, Number(s.impactPct) || 0)),
          tone: (s.tone === 'good' || s.tone === 'warn' || s.tone === 'bad'
            ? s.tone
            : 'warn') as 'good' | 'warn' | 'bad',
          body: String(s.body || '')
        }));
      if (!scores.length) {
        return { data: { error: 'No scores provided.' } };
      }
      const block: RenderBlock = {
        kind: 'health-impact',
        title: args.title ? String(args.title) : 'Human impact',
        subtitle: args.subtitle ? String(args.subtitle) : undefined,
        scores
      };
      return {
        data: { scoreCount: scores.length },
        display: [block],
        note: 'Human-impact card rendered.'
      };
    },

    async render_actions(args): Promise<ToolResult> {
      const items = ((args.items as any[]) || [])
        .filter((it) => it && typeof it.title === 'string')
        .map((it) => ({
          priority: (it.priority === 'high' || it.priority === 'low' ? it.priority : 'med') as
            | 'high'
            | 'med'
            | 'low',
          title: String(it.title),
          body: String(it.body || ''),
          parameter: it.parameter ? String(it.parameter) : undefined
        }));
      if (!items.length) {
        return { data: { error: 'No action items provided.' } };
      }
      const block: RenderBlock = {
        kind: 'actions',
        title: args.title ? String(args.title) : 'Next steps',
        subtitle: args.subtitle ? String(args.subtitle) : undefined,
        items
      };
      return {
        data: { itemCount: items.length },
        display: [block],
        note: 'Action recommendations rendered.'
      };
    },

    async read_uploaded_dataset(): Promise<ToolResult> {
      const ud = access.uploadedData;
      if (!ud) {
        return {
          data: {
            error:
              'No file is currently attached. Ask the user to click the paperclip and upload a CSV or Excel file.'
          }
        };
      }
      const sample = ud.rows.slice(0, 8);
      const cleanSample = sample.map((row) =>
        Object.fromEntries(
          ud.headers.map((h, i) => [h, row[i] === undefined ? null : row[i]])
        )
      );
      return {
        data: {
          filename: ud.filename,
          row_count: ud.rows.length,
          headers: ud.headers,
          sample_rows: cleanSample,
          uploaded_at: new Date(ud.uploadedAt).toISOString()
        },
        note: `Uploaded file: ${ud.filename} — ${ud.rows.length} rows, columns: ${ud.headers.join(', ')}.`
      };
    },

    async export_data(args): Promise<ToolResult> {
      const params = ((args.parameters as string[]) || []).filter(Boolean);
      if (!params.length) {
        return { data: { error: 'parameters is required.' } };
      }
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const format = args.format === 'json' ? 'json' : 'csv';
      const buckets = await fetchBuckets(win, scope.serials);

      type Row = {
        timestamp: string;
        device: string;
        parameter: string;
        value: number;
      };
      const rows: Row[] = [];
      Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
        const dev = findDevice(deviceKey);
        if (!include(dev, deviceKey)) return;
        const name = deviceLabel(dev, deviceKey);
        (deviceArr || []).forEach((s: any) => {
          if (!params.includes(s.name)) return;
          (s.data || []).forEach(([ts, v]: [number, number]) => {
            if (!Number.isFinite(v) || v === 0) return;
            rows.push({
              timestamp: new Date(ts).toISOString(),
              device: name,
              parameter: s.name,
              value: round(v, 4)
            });
          });
        });
      });
      if (!rows.length) {
        return {
          data: {
            error: `No data points in ${win.label}.`,
            ...windowEcho(win),
            ...deviceEcho(scope)
          }
        };
      }
      rows.sort((a, b) =>
        a.timestamp === b.timestamp
          ? a.device.localeCompare(b.device)
          : a.timestamp.localeCompare(b.timestamp)
      );

      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const paramStem = params.join('_');
      // Name the file after the window it covers: the dates for an explicit
      // range, otherwise the lookback length.
      const windowStem = win.absolute
        ? `${new Date(win.startDate).toISOString().slice(0, 10)}_to_${new Date(win.endDate)
            .toISOString()
            .slice(0, 10)}`
        : `${win.periodHours}h`;
      let content: string;
      let mime: string;
      let filename: string;
      if (format === 'json') {
        content = JSON.stringify(rows, null, 2);
        mime = 'application/json';
        filename = `telosair_${paramStem}_${windowStem}_${stamp}.json`;
      } else {
        const csvEscape = (s: string): string =>
          /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        const lines = ['timestamp,device,parameter,value'];
        rows.forEach((r) => {
          lines.push(
            [r.timestamp, csvEscape(r.device), r.parameter, String(r.value)].join(',')
          );
        });
        content = lines.join('\n');
        mime = 'text/csv';
        filename = `telosair_${paramStem}_${windowStem}_${stamp}.csv`;
      }

      const block: RenderBlock = {
        kind: 'download',
        title: 'Data export ready',
        filename,
        content,
        mime,
        byteCount: content.length,
        description: `${params.length} parameter${params.length > 1 ? 's' : ''} (${params.join(
          ', '
        )}) over ${win.label} — ${rows.length.toLocaleString()} rows.`
      };

      return {
        data: {
          parameters: params,
          ...windowEcho(win),
          ...deviceEcho(scope),
          row_count: rows.length,
          format,
          filename
        },
        display: [block],
        note: withScopeNotes(
          withWindowNotes(
            `Exported ${rows.length} rows for ${win.label}. Click the download card to save.`,
            win
          ),
          scope
        )
      };
    },

    async update_chart_appearance(args): Promise<ToolResult> {
      // Only forward fields the model actually set; the page applies
      // clampChartSettings so anything invalid is silently corrected.
      const partial: Partial<ChartSettings> = {};
      if (typeof args.fontSize === 'number') partial.fontSize = args.fontSize;
      if (typeof args.tickColor === 'string') partial.tickColor = args.tickColor;
      if (typeof args.gridColor === 'string') partial.gridColor = args.gridColor;
      if (typeof args.seriesColor === 'string') partial.seriesColor = args.seriesColor;
      if (typeof args.showGrid === 'boolean') partial.showGrid = args.showGrid;
      if (typeof args.showLegend === 'boolean') partial.showLegend = args.showLegend;

      if (Object.keys(partial).length === 0) {
        return { data: { error: 'No appearance fields provided.' } };
      }

      access.updateChartSettings(partial);

      const summary = Object.entries(partial)
        .map(([k, v]) => {
          if (k === 'seriesColor' && v === '') return 'series colour → palette default';
          return `${k.replace(/([A-Z])/g, ' $1').toLowerCase()} → ${String(v)}`;
        })
        .join(', ');

      const block: RenderBlock = {
        kind: 'callout',
        tone: 'good',
        title: 'Chart appearance updated',
        body: summary
      };

      return {
        data: { applied: partial },
        display: [block],
        note: `Applied: ${summary}. All charts on the page now use these settings.`
      };
    },

    async get_my_location(): Promise<ToolResult> {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        return {
          data: {
            error:
              'Geolocation is not supported by this browser. Ask the user to name a city instead.'
          }
        };
      }
      try {
        // Prompts the browser's native location-permission dialog. Resolves
        // once the user allows and a fix is obtained; rejects on deny/timeout.
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 300000
          });
        });
        const lat = round(pos.coords.latitude, 5);
        const lon = round(pos.coords.longitude, 5);
        const accuracyM = Math.round(pos.coords.accuracy);
        return {
          data: { lat, lon, accuracy_m: accuracyM },
          note: `User's current location resolved: lat ${lat}, lon ${lon} (±${accuracyM} m). Use these coordinates for fetch_global_air_quality / compare_to_global — do not ask for a city.`
        };
      } catch (e: any) {
        const code = e?.code;
        let reason = 'Could not determine your location.';
        if (code === 1) reason = 'Location permission was denied.';
        else if (code === 2) reason = 'Your location is currently unavailable.';
        else if (code === 3) reason = 'The location request timed out.';
        return {
          data: {
            error: `${reason} Ask the user to type a city name instead, then retry with that.`
          }
        };
      }
    },

    async fetch_global_air_quality(args): Promise<ToolResult> {
      const lat = args.lat !== undefined ? Number(args.lat) : undefined;
      const lon = args.lon !== undefined ? Number(args.lon) : undefined;
      const city = (args.city as string) || (args.location as string) || undefined;
      if (lat === undefined && lon === undefined && !city) {
        return {
          data: { error: 'Provide a city name, or lat and lon, to look up worldwide air quality.' }
        };
      }
      const radiusKm = args.radius_km !== undefined ? Number(args.radius_km) : undefined;
      const source = (args.source as 'auto' | 'openaq' | 'waqi') || 'auto';

      const resp = await getGlobalAirQuality({ lat, lon, city, radiusKm, source });
      const payload = resp?.data || {};
      const { aqi } = payload;
      const stations: any[] = Array.isArray(payload.stations) ? payload.stations : [];
      const notes: string[] = Array.isArray(payload.notes) ? payload.notes : [];

      const placeLabel =
        (args.location as string) || city || aqi?.stationName || stations[0]?.name || 'this location';

      const cards: MetricCard[] = [];
      if (aqi && Number.isFinite(Number(aqi.value))) {
        cards.push({
          label: 'AQI (WAQI)',
          value: String(Math.round(Number(aqi.value))),
          unit: aqi.dominantPollutant ? `dom. ${aqi.dominantPollutant}` : undefined,
          tone: 'neutral'
        });
      }
      const nearest = stations[0];
      if (nearest) {
        (nearest.measurements || []).slice(0, 5).forEach((m: any) => {
          if (!Number.isFinite(Number(m.value))) return;
          cards.push({
            label: String(m.parameter).toUpperCase(),
            value: round(Number(m.value), 2).toString(),
            unit: m.unit || undefined,
            tone: 'neutral'
          });
        });
      }

      const display: RenderBlock[] = [];
      if (cards.length) {
        display.push({ kind: 'metrics', title: `Outdoor air — ${placeLabel}`, cards: cards.slice(0, 6) });
      }
      if (!cards.length && notes.length) {
        display.push({
          kind: 'callout',
          tone: 'warn',
          title: 'No worldwide data available',
          body: notes.join(' ')
        });
      }

      return {
        data: {
          location: placeLabel,
          aqi: aqi
            ? { value: aqi.value, dominantPollutant: aqi.dominantPollutant, station: aqi.stationName, time: aqi.time }
            : undefined,
          stations: stations.map((s) => ({
            name: s.name,
            distance_km: s.distanceKm,
            measurements: (s.measurements || []).map((m: any) => ({
              parameter: m.parameter,
              value: round(Number(m.value), 3),
              unit: m.unit
            }))
          })),
          notes
        },
        display: display.length ? display : undefined,
        note: cards.length
          ? `Worldwide air quality for ${placeLabel}. This is EXTERNAL reference data, not the tenant's sensors.${notes.length ? ` Notes: ${notes.join(' ')}` : ''}`
          : `No worldwide data returned. ${notes.join(' ')}`
      };
    },

    async compare_to_global(args): Promise<ToolResult> {
      const param = String(args.parameter || '');
      if (!param) return { data: { error: 'parameter is required.' } };
      const lat = args.lat !== undefined ? Number(args.lat) : undefined;
      const lon = args.lon !== undefined ? Number(args.lon) : undefined;
      const city = (args.city as string) || undefined;
      const hasCoords = Number.isFinite(lat) && Number.isFinite(lon);
      if (!hasCoords && !city) {
        return { data: { error: 'Provide a city name, or lat and lon, to find nearby stations.' } };
      }
      const win = resolveWindow(args);
      const scope = resolveDeviceScope(args, access.devices);
      const include = deviceGate(args, scope);
      const radiusKm = args.radius_km !== undefined ? Number(args.radius_km) : undefined;
      const unit = paramUnit(param);

      // Local: average the parameter across the tenant's sensors for the window.
      const buckets = await fetchBuckets(win, scope.serials);
      const localVals: number[] = [];
      Object.entries(buckets).forEach(([deviceKey, deviceArr]: [string, any]) => {
        const dev = findDevice(deviceKey);
        if (!include(dev, deviceKey)) return;
        (deviceArr || []).forEach((s: any) => {
          if (s.name !== param) return;
          (s.data || []).forEach(([, v]: [number, number]) => {
            if (Number.isFinite(v) && v !== 0) localVals.push(v);
          });
        });
      });
      const localAvg = localVals.length
        ? localVals.reduce((a, b) => a + b, 0) / localVals.length
        : undefined;

      // Global: raw concentrations from OpenAQ near the site. A city name is
      // geocoded to coordinates server-side (via WAQI) when lat/lon are omitted.
      const gResp = await getGlobalAirQuality({ lat, lon, city, radiusKm, source: 'openaq' });
      const gPayload = gResp?.data || {};
      const stations: any[] = Array.isArray(gPayload.stations) ? gPayload.stations : [];
      const notes: string[] = Array.isArray(gPayload.notes) ? gPayload.notes : [];

      const globalHits = stations
        .map((s) => {
          const m = matchGlobalMeasurement(s, param);
          return m ? { name: s.name, value: Number(m.value), unit: m.unit } : null;
        })
        .filter((x): x is { name: string; value: number; unit: string } => !!x && Number.isFinite(x.value));

      let resultNote: string;
      if (localAvg === undefined) {
        resultNote = `No local sensor data for ${PRETTY_NAME[param] || param} over ${win.label}; showing nearby stations only. ${notes.join(' ')}`;
      } else if (globalHits.length) {
        resultNote = `Compared local ${PRETTY_NAME[param] || param} over ${win.label} to ${globalHits.length} nearby public station(s).`;
      } else {
        resultNote = `No nearby public station reported ${PRETTY_NAME[param] || param}. ${notes.join(' ')}`;
      }
      // Public networks only expose their LATEST reading, so a historical local
      // window is not a like-for-like comparison — say so rather than implying
      // the station numbers come from the same dates.
      if (win.absolute && Date.now() - win.endDate > 6 * HOUR_MS) {
        notes.push(
          `Your sensor average covers ${win.label}, but the public station values are their CURRENT readings — not from that period. Say so when presenting the comparison.`
        );
      }

      // Public gas readings often use ppm where our sensors use ppb (or vice
      // versa); plotting them on one axis would be off by 1000×. Detect a unit
      // mismatch (PM mass units normalize to the same token) and warn instead of
      // silently mis-scaling.
      const normUnit = (u: string): string =>
        String(u || '')
          .toLowerCase()
          .replace(/µ/g, 'u')
          .replace(/³/g, '3')
          .replace(/[\s^/]/g, '');
      const mismatched = globalHits.find(
        (g) => g.unit && normUnit(g.unit) !== normUnit(unit)
      );
      if (mismatched) {
        notes.push(
          `Unit mismatch: your ${PRETTY_NAME[param] || param} is in "${unit}" but the public station reports "${mismatched.unit}" — values are not directly comparable, interpret with care.`
        );
      }

      if (localAvg === undefined && !globalHits.length) {
        return {
          data: { error: 'No local or nearby worldwide data for this parameter.', notes }
        };
      }

      const localLabel = String(args.city ? `Your sensors — ${args.city}` : 'Your sensors');
      // Distinguish "us" (the tenant's own sensors) from the "city" reference
      // stations with two colours: your reading uses the parameter's own colour,
      // the outdoor stations a muted slate.
      const YOURS_COLOR = paramColor(param);
      const CITY_COLOR = '#64748B';
      const categories: string[] = [];
      const values: number[] = [];
      const barColors: string[] = [];
      if (localAvg !== undefined) {
        categories.push(localLabel);
        values.push(round(localAvg, 2));
        barColors.push(YOURS_COLOR);
      }
      globalHits.slice(0, 5).forEach((g) => {
        categories.push(g.name);
        values.push(round(g.value, 2));
        barColors.push(CITY_COLOR);
      });

      const label = PRETTY_NAME[param] || param;
      const block: RenderBlock = {
        kind: 'bar-chart',
        title: `${label} — your sensors vs nearby outdoor stations`,
        categories,
        series: [{ name: label, color: paramColor(param), colors: barColors, unit, values }],
        unit,
        orientation: 'horizontal'
      };

      const display: RenderBlock[] = [block];
      if (localAvg !== undefined && globalHits.length) {
        const nearest = globalHits[0];
        const deltaPct = nearest.value
          ? round(((localAvg - nearest.value) / nearest.value) * 100, 0)
          : 0;
        const cleaner = localAvg < nearest.value;
        display.push({
          kind: 'callout',
          tone: cleaner ? 'good' : 'warn',
          title: `Local ${label} is ${Math.abs(deltaPct)}% ${cleaner ? 'lower' : 'higher'} than ${nearest.name}`,
          body: `Your sensors averaged ${round(localAvg, 2)} ${unit} over ${win.label} vs ${round(nearest.value, 2)} ${unit} at the nearest public station.`
        });
      }

      return {
        data: {
          parameter: param,
          unit,
          ...windowEcho(win),
          ...deviceEcho(scope),
          local_avg: localAvg !== undefined ? round(localAvg, 2) : null,
          local_sample_count: localVals.length,
          global_stations: globalHits.map((g) => ({ name: g.name, value: round(g.value, 2), unit: g.unit })),
          notes
        },
        display,
        note: resultNote
      };
    }
  };

  return executors;
}

